import { useState } from 'react';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { Button, Input, Modal } from '@/component';
import type { SongMatch } from '@/hook';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';

interface CorrectSongModalProps {
  trackId: Id<'tracks'>;
  match: SongMatch | null;
  onClose: () => void;
}

/** "Not this song?": the uploader names the song their track really is. The
 * correction is manual and beats every later enrichment of the track. */
export function CorrectSongModal({ trackId, match, onClose }: CorrectSongModalProps) {
  const [title, setTitle] = useState(match?.title ?? '');
  const [artist, setArtist] = useState(match?.artist ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    const result = await toResult(() =>
      convex.mutation(api.songs.correct, { trackId, title, artist: artist.trim() || undefined })
    );
    setBusy(false);
    if (result.ok) onClose();
    else setError(result.error.message);
  }

  return (
    <Modal open onClose={onClose} title="Which song is this?" kicker="Correct the match" maxWidth={520}>
      <p className="mt-3 max-w-[44ch] text-sm leading-[1.5] text-mute">
        Name the song and its original artist. Your answer is kept over anything we match later.
      </p>
      <div className="mt-[22px] flex flex-col gap-[18px]">
        <Input label="Song title" value={title} onChange={(event) => setTitle(event.target.value)} className="bg-bg" />
        <Input label="Artist" value={artist} onChange={(event) => setArtist(event.target.value)} className="bg-bg" />
      </div>
      <div className="mt-7 flex flex-wrap gap-[10px]">
        <Button onClick={() => void save()} disabled={busy || !title.trim()} className="px-[18px]">
          Save match
        </Button>
        <Button variant="outline" quiet onClick={onClose} className="px-[17px]">
          Cancel
        </Button>
      </div>
      {error && <p className="mt-4 text-[13px] text-down">{error}</p>}
    </Modal>
  );
}
